import { protectedFormReq, protectedReq, request } from '../helpers/requestHelper';
import { types } from '../types/types';

const getResponseBody = async response => {
    try {
        return await response.json();
    } catch (error) {
        return {
            status: false,
            msg: 'request-err'
        };
    }
};

export const getPublicProductReviews = async (
    productId,
    { page = 1, limit = 10, sort = 'newest', variantId = '' } = {}
) => {
    const params = new URLSearchParams({
        page,
        limit,
        sort
    });

    if (variantId)
        params.set('variant_id', variantId);

    const response = await request(
        `reviews/product/${productId}?${params.toString()}`
    );

    return await getResponseBody(response);
};

export const getReviewEligibility = async (productId, variantId) => {
    const response = await protectedReq(
        `reviews/eligibility/${productId}/${variantId}`
    );

    return await getResponseBody(response);
};

const buildReviewForm = ({
    productId,
    variantId,
    rating,
    comment,
    images = [],
    removedImageIds = []
}) => {
    const form = new FormData();
    form.append('product_id', productId);
    form.append('variant_id', variantId);
    form.append('rating', rating);
    form.append('comment', comment);
    form.append('removed_image_ids', JSON.stringify(removedImageIds));

    for (const image of images)
        form.append('images', image);

    return form;
};

export const saveCustomerReview = async model => {
    const form = buildReviewForm(model);
    const endpoint = model.reviewId ?
        `reviews/${model.reviewId}`
    :
        'reviews';
    const method = model.reviewId ? 'PUT' : 'POST';
    const response = await protectedFormReq(endpoint, form, method);

    return await getResponseBody(response);
};

export const deleteCustomerReview = async reviewId => {
    const response = await protectedReq(
        `reviews/${reviewId}`,
        {},
        'DELETE'
    );

    return await getResponseBody(response);
};

const getCustomerReviewRequest = async (endpoint, params = {}) => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== '' && value !== undefined && value !== null)
            query.set(key, value);
    });

    const suffix = query.toString() ? `?${query.toString()}` : '';
    const response = await protectedReq(`${endpoint}${suffix}`);

    return await getResponseBody(response);
};

export const getMyReviewSummary = async () => {
    return await getCustomerReviewRequest('reviews/mine/summary');
};

export const getMyPendingReviews = async filters => {
    return await getCustomerReviewRequest(
        'reviews/mine/pending',
        filters
    );
};

export const getMyReviews = async filters => {
    return await getCustomerReviewRequest(
        'reviews/mine/list',
        filters
    );
};

export const reviewErrorText = reason => {
    const messages = {
        already_reviewed: 'Ya publicaste una reseña para esta variante.',
        'invalid-token': 'Tu sesión dejó de ser válida. Inicia sesión nuevamente.',
        'unavailable-token': 'Inicia sesión nuevamente para continuar.',
        not_delivered: 'Podrás reseñar esta variante cuando tu pedido sea entregado.',
        not_purchased: 'No encontramos una compra de esta variante en tu cuenta.',
        payment_not_approved: 'La compra todavía no cuenta con un pago aprobado.',
        product_not_found: 'No se encontró el producto.',
        'product-not-found': 'No se encontró el producto.',
        request_err: 'No se pudo completar la petición.',
        'request-err': 'No se pudo completar la petición.',
        'review-comment-invalid': 'El comentario debe tener entre 6 y 2000 caracteres.',
        'review-comment-html': 'El comentario no puede incluir etiquetas HTML.',
        'review-id-invalid': 'La información del producto no es válida.',
        'review-image-content': 'Uno de los archivos no contiene una imagen válida.',
        'review-image-limit': 'Puedes incluir hasta 6 imágenes.',
        'review-image-too-large': 'Cada imagen debe pesar como máximo 5 MB.',
        'review-image-type': 'Sólo se permiten imágenes JPEG, PNG o WebP.',
        'review-images-invalid': 'La selección de imágenes no es válida.',
        'review-not-found': 'No se encontró la reseña o no tienes permiso para modificarla.',
        'review-pagination-invalid': 'La página solicitada no es válida.',
        'review-rating-invalid': 'Selecciona una calificación de 1 a 5 estrellas.',
        'review-rate-limit': 'Has realizado demasiadas solicitudes. Inténtalo más tarde.',
        'review-search-invalid': 'La búsqueda es demasiado larga.',
        'review-sort-invalid': 'El orden seleccionado no es válido.',
        'review-status-invalid': 'El estado seleccionado no es válido.',
        'review-storage-unavailable': 'El almacenamiento de imágenes no está disponible.',
        'server-err': 'Ocurrió un incidente. Inténtalo nuevamente.',
        variant_not_found: 'No se encontró la variante seleccionada.',
        'variant-not-found': 'No se encontró la variante seleccionada.'
    };

    return messages[reason] || 'No se pudo completar la operación.';
};

export const reviewSummaryUpdated = (productId, summary) => ({
    type: types.reviewSummaryUpdated,
    payload: {
        productId,
        summary
    }
});

const adminReviewRequest = async (endpoint, body = {}, method = 'GET') => {
    const response = await protectedReq(endpoint, body, method);
    return await getResponseBody(response);
};

export const getAdminReviewDashboard = async (range = '90d') => {
    return await adminReviewRequest(
        `product/reviews/dashboard?range=${encodeURIComponent(range)}`
    );
};

export const getAdminProductReviews = async (filters = {}) => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== undefined && value !== null)
            params.set(key, value);
    });

    return await adminReviewRequest(
        `product/reviews/list?${params.toString()}`
    );
};

export const moderateAdminReview = async (reviewId, model) => {
    return await adminReviewRequest(
        `product/reviews/${reviewId}/moderation`,
        model,
        'PATCH'
    );
};

export const saveAdminReviewReply = async (reviewId, text) => {
    return await adminReviewRequest(
        `product/reviews/${reviewId}/reply`,
        { text },
        'PUT'
    );
};

export const withdrawAdminReviewReply = async reviewId => {
    return await adminReviewRequest(
        `product/reviews/${reviewId}/reply`,
        {},
        'DELETE'
    );
};
